/**
 * Starting a build.
 *
 * Two extra choices appear only when they mean something: the platform, where the deployment can
 * build both, and which Mac, where iOS is chosen. An Android build has neither -- it runs in the
 * container and there is nothing to pick.
 *
 * A Mac that is away can still be chosen. Someone whose machine is asleep will wake it, and the
 * job waits; refusing the choice would be tidier and less useful.
 */
import { useState } from 'react';
import type { Agent, Client, Platforms, Template } from '../api';

type Platform = 'android' | 'ios';

export function BuildForm({ client, template, platforms, macs, onBuild }: {
  client: Client;
  template: Template | null;
  platforms: Platforms | null;
  macs: Agent[];
  onBuild: (options: {
    ref?: string;
    skipSmoke?: boolean;
    upload?: boolean;
    platform: Platform;
    requestedAgentId?: number | null;
  }) => void;
}) {
  const bothPlatforms = Boolean(platforms?.android && platforms?.ios);
  const [platform, setPlatform] = useState<Platform>(platforms?.android ? 'android' : 'ios');
  const [ref, setRef] = useState('');
  const [macId, setMacId] = useState<string>('');
  const [upload, setUpload] = useState(false);

  const iosMacs = macs.filter((mac) => mac.platforms.includes('ios'));
  const ready = iosMacs.filter((mac) => mac.online);
  // Delivery is an iOS thing and needs both credentials; the choice does not exist otherwise.
  const canDeliver = platform === 'ios' && client.hasIosSigning && client.hasAppStoreKey;
  const start = (skipSmoke: boolean) => onBuild({
    platform,
    ...(ref ? { ref } : {}),
    ...(skipSmoke ? { skipSmoke: true } : {}),
    ...(canDeliver && upload ? { upload: true } : {}),
    ...(platform === 'ios' && macId ? { requestedAgentId: Number(macId) } : {}),
  });

  return (
    <>
      {bothPlatforms && (
        <div className="platform-pick" role="group" aria-label="Platform">
          {(['android', 'ios'] as const).map((choice) => (
            <button
              key={choice}
              type="button"
              className={platform === choice ? 'on' : ''}
              aria-pressed={platform === choice}
              onClick={() => setPlatform(choice)}
            >
              {choice === 'android' ? 'Android' : 'iOS'}
            </button>
          ))}
        </div>
      )}

      <div className="actions" style={{ marginBottom: 10 }}>
        <button type="button" className="btn" onClick={() => start(false)}>
          {canDeliver && upload ? 'Build and send' : 'Build'}
        </button>
        <button type="button" className="btn quiet" onClick={() => start(true)}>Skip smoke test</button>
      </div>

      {canDeliver && (
        <label className="rail-check" htmlFor="build-upload">
          <input id="build-upload" type="checkbox" checked={upload}
                 onChange={(event) => setUpload(event.target.checked)} />
          Send to App Store Connect
          {upload && (
            <span className="rail-note">
              Apple keeps build {client.nextVersionCode} once it accepts it.
            </span>
          )}
        </label>
      )}

      {platform === 'ios' && (
        <label htmlFor="build-mac" className="rail-field">
          on
          <select id="build-mac" value={macId} onChange={(event) => setMacId(event.target.value)}>
            <option value="">
              {ready.length ? `any free Mac (${ready.length} ready)` : 'any free Mac'}
            </option>
            {iosMacs.map((mac) => (
              <option key={mac.id} value={mac.id} disabled={!mac.enabled}>
                {mac.name}
                {!mac.enabled ? ' — paused' : mac.online ? '' : ' — away'}
              </option>
            ))}
          </select>
          {iosMacs.length === 0 && <span className="rail-note">No Macs yet.</span>}
          {iosMacs.length > 0 && ready.length === 0 && (
            <span className="rail-note">None online. The build waits.</span>
          )}
        </label>
      )}

      {template && !template.error && platform === 'android' && (
        <label htmlFor="build-ref" className="rail-field">
          from
          <select id="build-ref" value={ref} onChange={(event) => setRef(event.target.value)}>
            <option value="">{client.templateRef ?? template.defaultRef}</option>
            {[...template.branches, ...template.tags]
              .filter((candidate) => candidate !== (client.templateRef ?? template.defaultRef))
              .map((candidate) => <option key={candidate} value={candidate}>{candidate}</option>)}
          </select>
        </label>
      )}

      {platform === 'ios' && platforms?.iosRef && (
        <span className="rail-note">from {platforms.iosRef}</span>
      )}
    </>
  );
}
